import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  string as stringType,
  shape,
  typename,
  namedFragment
} from '@thd-nucleus/data-sources';
import classNames from 'classnames';
import './background-image.scss';

const TEXT_COLOR_CLASSES_MOBILE = [
  'max-md:[&_h1]:sui-text-[rgb(var(--sui-palette-white))]',
  'max-md:[&_.breadcrumb__item_a]:sui-text-[rgb(var(--sui-palette-white))]',
  'max-md:[&_.breadcrumb__item_span]:sui-text-[rgb(var(--sui-palette-white))]',
  'max-md:[&_.breadcrumb__item_div]:sui-text-[rgb(var(--sui-palette-white))]',
  'max-md:[&_h2]:sui-text-[rgb(var(--sui-palette-white))]'
].join(' ');

const TEXT_COLOR_CLASSES_DESKTOP = [
  'md:[&_h1]:sui-text-[rgb(var(--sui-palette-white))]',
  'md:[&_.breadcrumb__item_a]:sui-text-[rgb(var(--sui-palette-white))]',
  'md:[&_.breadcrumb__item_span]:sui-text-[rgb(var(--sui-palette-white))]',
  'md:[&_.breadcrumb__item_div]:sui-text-[rgb(var(--sui-palette-white))]',
  'md:[&_h2]:sui-text-[rgb(var(--sui-palette-white))]'
].join(' ');

const BG_OVERRIDES_CLASSES_MOBILE = [
  'max-md:[&_.sui-paper]:sui-bg-[rgb(var(--sui-palette-background-primary,var(--sui-palette-white)))]',
  'max-md:[&_div.sui-line-clamp-unset]:sui-bg-[rgb(var(--sui-palette-background-primary,var(--sui-palette-white)))]',
  'max-md:[&_div.sui-line-clamp-unset]:sui-p-4',
  'max-md:[&_.sui-paper_div.sui-line-clamp-unset]:!sui-p-0'
].join(' ');

const BG_OVERRIDES_CLASSES_DESKTOP = [
  'md:[&_.sui-paper]:sui-bg-[rgb(var(--sui-palette-background-primary,var(--sui-palette-white)))]',
  'md:[&_div.sui-line-clamp-unset]:sui-bg-[rgb(var(--sui-palette-background-primary,var(--sui-palette-white)))]',
  'md:[&_div.sui-line-clamp-unset]:sui-p-4',
  'md:[&_.sui-paper_div.sui-line-clamp-unset]:!sui-p-0'
].join(' ');

const BackgroundImage = ({
  children,
  desktopImage,
  mobileImage,
  scrollBehavior
}) => {
  const imageConfig = useMemo(() => {
    const desktopUrl = desktopImage?.damDownloadedContent?.url;
    const mobileUrl = mobileImage?.damDownloadedContent?.url;
    return {
      desktopUrl,
      mobileUrl,
      hasAnyImage: Boolean(desktopUrl || mobileUrl),
    };
  }, [desktopImage, mobileImage]);

  if (!imageConfig.hasAnyImage) {
    return children;
  }

  const isStatic = scrollBehavior === 'static';

  const wrapperClasses = classNames(
    'sui-relative sui-w-full sui-min-h-screen',
    imageConfig.mobileUrl && TEXT_COLOR_CLASSES_MOBILE,
    imageConfig.desktopUrl && TEXT_COLOR_CLASSES_DESKTOP,
    isStatic ? 'is-fixed' : 'is-scroll',
    imageConfig.desktopUrl && 'has-desktop-image',
    imageConfig.mobileUrl && 'has-mobile-image',
  );

  const positionClasses = isStatic
    ? 'sui-fixed sui-inset-0 sui-w-screen sui-h-screen sui-bg-cover sui-bg-center sui-bg-no-repeat sui-transform sui-translate-x-0 sui-translate-y-0 sui-translate-z-0 sui-will-change-transform sui-backface-hidden [perspective:1000px]'
    : 'sui-absolute sui-inset-0 sui-w-full sui-h-full sui-bg-scroll sui-bg-[length:auto_100vh] sui-bg-[position:top_left] sui-bg-repeat';

  const layerClasses = classNames(
    'sui-z-0 sui-bg-white sui-pointer-events-none',
    'background-image-layer',
    positionClasses,
    'supports-[(-webkit-touch-callout:none)]:fixed',
    'supports-[(-webkit-touch-callout:none)]:translate-z-0'
  );

  const layerStyle = {
    ...(imageConfig.desktopUrl && { '--bg-img-desktop': `url(${imageConfig.desktopUrl})` }),
    ...(imageConfig.mobileUrl && { '--bg-img-mobile': `url(${imageConfig.mobileUrl})` })
  };

  const contentClasses = classNames(
    'sui-relative sui-z-10',
    imageConfig.mobileUrl && BG_OVERRIDES_CLASSES_MOBILE,
    imageConfig.desktopUrl && BG_OVERRIDES_CLASSES_DESKTOP,
  );

  return (
    <div
      className={wrapperClasses}
      data-testid="background-image-wrapper"
      data-scroll-behavior={scrollBehavior}
    >
      <div
        className={layerClasses}
        style={layerStyle}
        aria-hidden="true"
      />
      <div className={contentClasses}>
        {children}
      </div>
    </div>
  );
};

BackgroundImage.displayName = 'BackgroundImage';

BackgroundImage.propTypes = {
  children: PropTypes.node,
  desktopImage: shape({}),
  mobileImage: shape({}),
  scrollBehavior: PropTypes.oneOf(['static', 'scroll']),
};

BackgroundImage.defaultProps = {
  children: null,
  desktopImage: null,
  mobileImage: null,
  scrollBehavior: 'static',
};

const BackgroundImageDamFragment = namedFragment({
  inline: false,
  fragmentType: 'DamMedia',
  fragmentAlias: 'BackgroundImageDamMedia'
}).shape({
  damDownloadedContent: shape({
    url: stringType(),
    fileName: stringType(),
    id: stringType(),
    description: stringType()
  }),
  __typename: typename('DamMedia')
});

export const backgroundImageField = shape({
  // id: stringType(),
  internalName: stringType(),
  scrollBehavior: stringType(),
  desktopImage: BackgroundImageDamFragment,
  mobileImage: BackgroundImageDamFragment,
  __typename: typename('ConfigBackgroundImage')
});

export default BackgroundImage;